import { useState } from 'react';
import { CircleCheck, CircleX, PlugZap, Radio, RefreshCw, Trash } from 'lucide-react';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { env } from '@/lib/env';
import { isEmbeddedSignupConfigured } from '@/lib/meta-sdk';
import type { ChannelSummary, TestResult } from '@/types/integrations';
import { ChannelCredentialsDialog } from './ChannelCredentialsDialog';
import type { ChannelSpec } from './integrations-catalog';
import { useDisconnectChannel, useTestChannel } from './use-integrations';
import { useStartMetaOAuth } from './use-meta-oauth';
import { WhatsAppSignupButton } from './WhatsAppSignupButton';

/**
 * Tarjeta de un canal de mensajería (WhatsApp, Messenger e Instagram). TikTok sigue soportado en el
 * backend pero no se ofrece en el panel (20-sep-2026).
 *
 * Tres acciones sobre el contrato real del backend:
 *  - __Conectar a mano__: `PUT .../integrations/channels/:channel` con las credenciales del
 *    canal (ver `ChannelCredentialsDialog`).
 *  - __Probar conexión__: `POST .../channels/:channel/test` → siempre 200 con `{ ok, message }`
 *    (llama a la Graph API de Meta). En WhatsApp, además del
 *    número, comprueba que el WABA esté **suscrito a esta app** (sin eso no llegan los mensajes).
 *  - __Desconectar__: `DELETE .../channels/:channel` (borra las credenciales cifradas).
 *
 * Messenger e Instagram además se pueden conectar con Facebook Login (OAuth): el botón manda el
 * navegador a la `authorizeUrl` que devuelve el backend, y al volver el panel lee el resultado
 * de la URL (`IntegrationsPage` + `meta-oauth-redirect.ts`).
 *
 * WhatsApp tiene su propio botón de **Embedded Signup** (`WhatsAppSignupButton`): abre la ventana de
 * Meta y el cliente conecta su WABA y su número sin pegar tokens.
 */

export interface ChannelCardProps {
  spec: ChannelSpec;
  /** Estado del canal según el resumen; `null` = nunca se conectó. */
  summary: ChannelSummary | null;
  canEdit: boolean;
}

export function ChannelCard({ spec, summary, canEdit }: ChannelCardProps) {
  const { tenantId } = useActiveTenantAccess();
  const disconnectChannel = useDisconnectChannel(tenantId);
  const testChannel = useTestChannel(tenantId);
  const startOAuth = useStartMetaOAuth(tenantId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const Icon = spec.icon;
  const configured = Boolean(summary?.configurado);
  const active = configured && Boolean(summary?.activo);

  // El pie que anuncia la conexión con Facebook solo se pinta si ese camino existe de verdad: para
  // Messenger/Instagram siempre (su OAuth es del backend) y para WhatsApp solo si el despliegue tiene
  // el Embedded Signup configurado. Si no, prometía un botón que no está (aviso engañoso, 20-sep-2026).
  const signupConFacebook =
    spec.channel === 'whatsapp'
      ? isEmbeddedSignupConfigured(env.metaAppId, env.metaEmbeddedSignupConfigId)
      : Boolean(spec.oauthChannel);

  async function handleTest() {
    setError(null);
    setTestResult(null);
    try {
      setTestResult(await testChannel.mutateAsync({ channel: spec.channel }));
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  async function handleDisconnect() {
    setError(null);
    setTestResult(null);
    try {
      await disconnectChannel.mutateAsync(spec.channel);
      setConfirmDisconnect(false);
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  async function handleOAuth() {
    if (!spec.oauthChannel) return;

    setError(null);
    try {
      const { authorizeUrl } = await startOAuth.mutateAsync(spec.oauthChannel);
      // Salida del panel hacia Facebook (el callback del backend devuelve a esta pantalla).
      window.location.assign(authorizeUrl);
    } catch (caught) {
      setError(getErrorMessage(caught));
    }
  }

  return (
    <>
      <Card>
        <CardHeader
          actions={
            <Badge
              tone={active ? 'success' : configured ? 'warning' : 'neutral'}
              dot
              icon={active ? <CircleCheck size={12} /> : <CircleX size={12} />}
            >
              {active ? 'Conectado' : configured ? 'Conectado inactivo' : 'Sin conectar'}
            </Badge>
          }
        >
          <CardTitle className="flex items-center gap-2">
            <span className="flex size-8 items-center justify-center rounded-control bg-brand-50 text-brand-600">
              <Icon size={16} />
            </span>
            {spec.label}
          </CardTitle>

          <CardDescription>{spec.description}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? <Alert>{error}</Alert> : null}

          {testResult ? (
            <Alert tone={testResult.ok ? 'success' : 'warning'}>{testResult.message}</Alert>
          ) : null}

          {spec.note ? <Alert tone="info">{spec.note}</Alert> : null}

          {canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={configured ? 'secondary' : 'primary'}
                leftIcon={<PlugZap size={16} />}
                onClick={() => setDialogOpen(true)}
              >
                {configured ? 'Reconfigurar' : 'Conectar'}
              </Button>

              {spec.channel === 'whatsapp' ? (
                <WhatsAppSignupButton conectado={configured} />
              ) : null}

              {spec.oauthChannel ? (
                <Button
                  variant="secondary"
                  leftIcon={<RefreshCw size={16} />}
                  loading={startOAuth.isPending}
                  onClick={() => void handleOAuth()}
                >
                  Conectar con Facebook
                </Button>
              ) : null}

              {configured ? (
                <Button
                  variant="secondary"
                  leftIcon={<Radio size={16} />}
                  loading={testChannel.isPending}
                  onClick={() => void handleTest()}
                >
                  Probar conexión
                </Button>
              ) : null}

              {configured ? (
                <Button
                  variant="ghost"
                  className="text-danger-strong hover:bg-danger-soft"
                  leftIcon={<Trash size={16} />}
                  disabled={disconnectChannel.isPending}
                  onClick={() => setConfirmDisconnect(true)}
                >
                  Desconectar
                </Button>
              ) : null}
            </div>
          ) : (
            <Alert tone="info">Solo un administrador del negocio puede conectar este canal.</Alert>
          )}

          {signupConFacebook ? (
            <p className="text-micro text-ink-faint">
              Con Facebook Login se conecta en dos clics (sin pegar tokens); la carga manual sigue
              disponible como respaldo.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <ChannelCredentialsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        spec={spec}
        configured={configured}
      />

      <ConfirmDialog
        open={confirmDisconnect}
        onClose={() => setConfirmDisconnect(false)}
        onConfirm={() => void handleDisconnect()}
        title={`Desconectar ${spec.label}`}
        description={`Se van a borrar las credenciales guardadas de ${spec.label}: el bot deja de recibir y responder por este canal hasta que lo vuelvas a conectar.`}
        confirmLabel={`Desconectar ${spec.label}`}
        loading={disconnectChannel.isPending}
        error={error}
      />
    </>
  );
}
